import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { DEFAULT_OG_IMAGE, SITE_NAME, buildCanonicalUrl } from './siteConfig';

/**
 * SeoManager: Sets meta tags for SEO (title, description, keywords, canonical, OG, Twitter, JSON-LD).
 */
const SeoManager = ({
  title,
  description,
  keywords,
  path,
  canonicalUrl,
  ogImage = DEFAULT_OG_IMAGE,
  structuredData,
  noindex = false,
  ogType = 'website',
}) => {
  const location = useLocation();

  const resolvedCanonical = useMemo(() => {
    if (canonicalUrl) return canonicalUrl;
    if (path) return buildCanonicalUrl(path);
    return buildCanonicalUrl(location.pathname);
  }, [canonicalUrl, path, location.pathname]);

  const jsonLd = useMemo(() => {
    if (!structuredData) return null;
    return Array.isArray(structuredData) ? structuredData : [structuredData];
  }, [structuredData]);

  return (
    <>
      <Helmet>
        {title && <title>{title}</title>}
        {description && <meta name="description" content={description} />}
        {keywords && <meta name="keywords" content={keywords} />}
        {noindex ? (
          <meta name="robots" content="noindex, nofollow" />
        ) : (
          <meta name="robots" content="index, follow" />
        )}

        <link rel="canonical" href={resolvedCanonical} />

        <meta property="og:site_name" content={SITE_NAME} />
        {title && <meta property="og:title" content={title} />}
        {description && <meta property="og:description" content={description} />}
        <meta property="og:url" content={resolvedCanonical} />
        <meta property="og:type" content={ogType} />
        {ogImage && <meta property="og:image" content={ogImage} />}

        <meta name="twitter:card" content="summary" />
        {title && <meta name="twitter:title" content={title} />}
        {description && <meta name="twitter:description" content={description} />}
        {ogImage && <meta name="twitter:image" content={ogImage} />}
      </Helmet>

      {jsonLd?.map((schema, index) => (
        <Helmet key={index}>
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>
      ))}
    </>
  );
};

export default SeoManager;
